import { Col, Row, Select, Table } from 'antd';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { PlusOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { formatCustomDataForTimeTable } from 'ontraccr-common/lib/Tasks';

import OnTraccrButton from '../../../common/buttons/OnTraccrButton';
import useToggle from '../../../common/hooks/useToggle';
import useCheckTableMaxRows from '../../../common/hooks/useCheckTableMaxRows';
import DisplayText from '../../../common/text/DisplayText';
import AddExistingBucketModal from '../../../buckets/AddExisingBucketDrawer';
import useValidateMaxSelect from '../../../common/hooks/useValidateMaxSelect';
import { getCalculationTableColumn, getCustomTableColumn, LINE_NUMBER_COLUMN } from './formFieldsHelpers';
import TableDeleteButton from './TableDeleteButton';
import OnTraccrTextInput from '../../../common/inputs/OnTraccrTextInput';
import { defaultFormatter, getSimpleSelectOptions, getTableInputParameters, simpleEquality } from './TimeEntryTable/TimeEntryTable.helpers';
import MemoizedTimeEntryTableInputComponent from './TimeEntryTable/TimeEntryTableInput';
import { uuid, getIdMap } from '../../../helpers/helpers';
import { getHasColumnPermissions } from '../../formHelpers';
import Permissions from '../../../auth/Permissions';
import {
  getABWorkStatusSelectOptions,
} from '../../../boards/BoardCardABWorkReporting/BoardCardABWorkReportingHelpers';
import { extractTextFromCell } from '../../../reports/Exports/exports.helpers';
import useOnDemandBucketCustomData from '../../../common/hooks/useOnDemandBucketCustomData';

const getBucketDefaultCustomData = (bucketTemplate) => (
  (bucketTemplate?.customFields?.fields ?? []).reduce((acc, field) => {
    acc[field.id] = field;
    return acc;
  }, {})
);

const getBucketRowCustomDataMap = (record) => (
  (record.customData?.fields ?? []).reduce((acc, field) => {
    acc[field.fieldId] = field.response;
    return acc;
  }, {})
);

const getBucketCustomDataResponse = (fieldType, updatedField) => {
  if (!updatedField) return updatedField;
  switch (fieldType) {
    case 'text':
    case 'attribute':
    case 'yes-no':
      return { value: updatedField.value };
    case 'dropdown':
      return { values: updatedField.values ?? [] };
    case 'dateTime':
      return {
        value: updatedField.value,
        startTime: updatedField.startTime,
        endTime: updatedField.endTime,
      };
    default:
      return updatedField;
  }
};

const getColumnMap = ({
  isDisplay,
  onValueUpdated,
  requiredColumns,
  preventEdits,
  bucketTemplate,
  responding,
  onUpdate,
  customColumnNames,
  relevantStatuses = {},
  shouldAllowStatusUpdates = () => true,
  shouldDisableHistorical = false,
}) => {
  const isReadOnly = isDisplay || preventEdits;
  const columns = {
    name: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.name}</div>,
      textTitle: customColumnNames.name,
      dataIndex: 'name',
      width: 125,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (val, record) => {
        if (isReadOnly) return val;
        return (
          <OnTraccrTextInput
            value={val}
            onChange={(e) => onValueUpdated(record.id, 'name', e.target.value)}
          />
        );
      },
      type: 'text',
      doNotCalculate: true,
    },
    description: {
      title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames.description}</div>,
      textTitle: customColumnNames.description,
      dataIndex: 'description',
      width: 125,
      sorter: (a, b) => a.description.localeCompare(b.description),
      render: (val, record) => {
        if (isReadOnly) return val;
        return (
          <OnTraccrTextInput
            value={val}
            onChange={(e) => onValueUpdated(record.id, 'description', e.target.value)}
          />
        );
      },
      type: 'text',
      doNotCalculate: true,
    },
    status: {
      title: <div className={requiredColumns && 'form-required-field'}>Status</div>,
      dataIndex: 'statusId',
      width: isReadOnly && !shouldAllowStatusUpdates() ? 125 : 200,
      render: (statusId, record) => {
        if (isReadOnly && !shouldAllowStatusUpdates(record)) return relevantStatuses[statusId]?.status;
        const currentLabel = relevantStatuses[statusId]?.status;
        const statusLocked = shouldDisableHistorical && currentLabel === 'Historical';
        const sourceStatuses = shouldDisableHistorical
          ? getABWorkStatusSelectOptions({ relevantStatuses, currentStatusId: statusId })
          : Object.values(relevantStatuses || {});
        const statusOptions = sourceStatuses.map((status) => ({
          value: status.id,
          label: status.status,
        }));
        return (
          <Select
            options={statusOptions}
            onChange={(value) => onValueUpdated(record.id, 'statusId', value)}
            value={statusId}
            style={{ width: '100%' }}
            disabled={statusLocked}
          />
        );
      },
    },
  };

  if (bucketTemplate?.customFields?.fields?.length) {
    const defaultCustomData = getBucketDefaultCustomData(bucketTemplate);

    bucketTemplate.customFields.fields.forEach((field) => {
      columns[field.id] = {
        title: <div className={requiredColumns && 'form-required-field'}>{customColumnNames[field.id]}</div>,
        textTitle: customColumnNames[field.id],
        dataIndex: field.id,
        type: field.type,
        dateOnly: !!field.configProps?.hideTime,
        render: (value, record) => {
          if (isReadOnly) {
            return extractTextFromCell(value);
          }

          const relevantValue = record.customData?.fields?.find(
            (customField) => customField.fieldId === field.id,
          );

          const {
            displayFormatter = defaultFormatter,
            areEqual = simpleEquality,
            getSelectOptions = getSimpleSelectOptions(field.type),
            value: parsedValue = value,
            inputType = 'text',
            readOnly = false,
          } = getTableInputParameters({
            value,
            entry: {
              customData: {
                [field.id]: relevantValue?.response,
              },
            },
            id: field.id,
            type: field.type,
            configProps: field.configProps,
          });

          return (
            <MemoizedTimeEntryTableInputComponent
              title={`Update ${customColumnNames[field.id]}`}
              task={{
                ...record,
                customData: getBucketRowCustomDataMap(record),
              }}
              fieldType={field.type}
              inputType={inputType}
              value={parsedValue}
              property={field.id}
              displayFormatter={displayFormatter}
              areEqual={areEqual}
              width={isReadOnly ? 'auto' : 240}
              onUpdate={onUpdate}
              configProps={field.configProps}
              responding={responding}
              readOnly={readOnly || isDisplay || field?.type === 'calculation'}
              getSelectOptions={getSelectOptions}
              defaultCustomData={defaultCustomData}
            />
          );
        },
      };
    });
  }

  return columns;
};

export const getColumns = ({
  columns,
  isDisplay,
  requiredColumns,
  preventEdits,
  onDelete,
  onValueUpdated,
  relevantBucketTemplate,
  responding,
  onUpdate,
  relevantStatuses,
  includeLineNumber = true,
  additionalColumnMap = {},
  shouldAllowStatusUpdates = () => true,
  shouldDisableHistorical = false,
  columnPermissions,
  userPositionId,
  responses = {},
}) => {
  const cols = includeLineNumber ? [LINE_NUMBER_COLUMN] : [];

  const customColumnNames = columns.reduce((acc, col) => {
    acc[col.key] = col.name;
    const secondaryColKey = col.key.split('.')?.pop();
    if (secondaryColKey) {
      acc[secondaryColKey] = col.name;
    }
    return acc;
  }, {});

  const colMap = getColumnMap({
    isDisplay,
    onValueUpdated,
    requiredColumns,
    preventEdits,
    bucketTemplate: relevantBucketTemplate,
    responding,
    onUpdate,
    customColumnNames,
    relevantStatuses,
    shouldAllowStatusUpdates,
    shouldDisableHistorical,
  });

  columns.forEach((col) => {
    const colVariants = [col.key];
    const secondaryColKey = col.key.split('.')?.pop();

    if (secondaryColKey) {
      colVariants.push(secondaryColKey);
    }

    let added = false;

    colVariants.forEach((variant) => {
      if (added) return;
      if (variant in colMap || variant in additionalColumnMap) {
        const matched = { ...(colMap[variant] || additionalColumnMap[variant]) };
        if (col.dataIndex) {
          matched.dataIndex = col.dataIndex;
        }

        if (col.name) {
          matched.name = col.name;
        }
        cols.push(matched);
        added = true;
      }
    });

    if (!added && col.isCustomText) {
      if (!getHasColumnPermissions({
        columnPermissions,
        column: col,
        userPositionId,
        userId: Permissions.id,
      })) {
        return;
      }
      cols.push(getCustomTableColumn(col, {
        isDisplay,
        preventEdits,
        requiredColumns,
        responses,
        onChange: (record, value) => onValueUpdated(record.id, col.key, value),
      }));
    }

    if (!added && col.isCalculation) {
      cols.push(getCalculationTableColumn(col, {
        isDisplay,
      }));
    }
  });
  if (!isDisplay) {
    cols.push({
      title: '',
      dataIndex: '',
      width: 100,
      render: (_, record) => (
        <TableDeleteButton onConfirm={() => onDelete(record.id)} />
      ),
    });
  }

  return cols;
};

export default function BucketTablePreview({
  title = null,
  buckets,
  bucketTemplates,
  columns,
  previewProps,
  setPreviewProps,
  isDisplay,
  id,
  responses,
  setResponses,
  responding,
  showCondensedView,
  configProps,
  statuses,
}) {
  const {
    subDataType,
    requiredColumns,
    preventEdits,
    columnPermissions = {},
  } = configProps ?? {};

  const positionNames = useSelector((state) => state.settings.positionNames);
  const users = useSelector((state) => state.users.users);
  const positionNameMap = useMemo(() => getIdMap(positionNames, 'name'), [positionNames]);
  const userMap = useMemo(() => getIdMap(users), [users]);
  const userPositionId = useMemo(() => {
    const userPosition = userMap[Permissions.id]?.position;
    return positionNameMap[userPosition]?.id;
  }, [positionNameMap, userMap]);

  const values = previewProps.values || []; // For Responses
  const {
    selected: previewSelected,
  } = previewProps;
  const {
    [id]: {
      values: responseSelected,
    } = {},
  } = responses;

  const rawSelected = responding ? responseSelected : previewSelected;

  const {
    toggle: toggleAddExistingBucketModal,
    isToggled: isAddExistingBucketModalVisible,
  } = useToggle();

  const rawDataSource = useMemo(() => (
    isDisplay && !responding ? values : rawSelected
  ), [isDisplay, responding, values, rawSelected]);

  const dataSource = useMemo(
    () => rawDataSource?.filter((row) => !row.isHidden),
    [rawDataSource],
  );

  const selectedBucketIds = useMemo(
    () => (dataSource ?? []).map((row) => row.bucketId ?? row.id),
    [dataSource],
  );
  useOnDemandBucketCustomData(selectedBucketIds);

  const relevantBucketTemplate = useMemo(() => (
    bucketTemplates.find((template) => template.id === subDataType)
  ), [bucketTemplates, subDataType]);

  const relevantBuckets = useMemo(() => (
    buckets.filter((bucket) => bucket.bucketTemplateId === subDataType)
  ), [buckets, subDataType]);

  const bucketMap = useMemo(() => (
    relevantBuckets.reduce((acc, bucket) => {
      acc[bucket.id] = bucket;
      return acc;
    }, {})
  ), [relevantBuckets]);

  const relevantStatuses = statuses[subDataType];

  const {
    maxExistingAllowed,
    shouldAddButtonBeEnabled = true,
  } = useCheckTableMaxRows({
    configProps,
    currentRowsLength: dataSource?.length,
  });

  const submitValidation = useCallback((selected) => (
    useValidateMaxSelect({
      maxExistingAllowed,
      selected,
      type: 'bucket',
    })
  ), [maxExistingAllowed]);

  const decorateBucket = useCallback((bucket) => {
    if (!bucket) return null;

    const baseBucket = { ...bucket };
    bucket.customData?.fields?.forEach((field) => {
      baseBucket[field.fieldId] = formatCustomDataForTimeTable({
        response: field.response,
        type: field.type,
        title: field.title,
      });
    });
    return baseBucket;
  }, []);

  const mergeRowBucketCustomData = useCallback((row) => {
    const bucketId = row.bucketId ?? row.id;
    const latestBucket = bucketMap[bucketId];
    if (!latestBucket?.customData) return row;

    const latestFields = latestBucket.customData.fields ?? [];
    const rowFields = row.customData?.fields ?? [];
    const fieldMap = new Map(latestFields.map((field) => [field.fieldId, field]));
    rowFields.forEach((field) => fieldMap.set(field.fieldId, field));

    return decorateBucket({
      ...row,
      customData: {
        ...latestBucket.customData,
        fields: Array.from(fieldMap.values()),
      },
    });
  }, [bucketMap, decorateBucket]);

  const enrichedDataSource = useMemo(
    () => (dataSource ?? []).map(mergeRowBucketCustomData),
    [dataSource, mergeRowBucketCustomData],
  );

  const updateSelected = useCallback((newSelected) => {
    const decoratedSelected = newSelected.map((bucket) => decorateBucket(bucket));
    if (responding) {
      setResponses({
        ...responses,
        [id]: {
          ...(responses[id]),
          values: decoratedSelected,
          columns,
        },
      });
    } else {
      setPreviewProps({
        ...previewProps,
        selected: decoratedSelected,
      });
    }
  }, [
    responding,
    responses,
    id,
    columns,
    previewProps,
    setResponses,
    setPreviewProps,
    decorateBucket,
  ]);

  useEffect(() => {
    if (!rawSelected?.length) return;

    let needsUpdate = false;
    const updated = rawSelected.map((row) => {
      const bucketId = row.bucketId ?? row.id;
      const latestBucket = bucketMap[bucketId];
      if (!latestBucket?.customData || row.customData != null) return row;
      needsUpdate = true;
      return mergeRowBucketCustomData(row);
    });

    if (needsUpdate) updateSelected(updated);
  }, [rawSelected, bucketMap, mergeRowBucketCustomData, updateSelected]);

  const onAdd = useCallback((newSelected) => {
    const newBuckets = newSelected.map((bucket) => ({
      ...(bucketMap[bucket] || {}),
      bucketId: bucket,
      id: uuid(),
    }));

    updateSelected((rawSelected || []).concat(newBuckets));
  }, [updateSelected, rawSelected, bucketMap]);

  const onValueUpdated = useCallback((rowId, key, value) => {
    const newSelected = rawSelected.map((row) => {
      if (row.id !== rowId) return row;
      const newRow = {
        ...row,
        [key]: value,
      };
      return newRow;
    });
    updateSelected(newSelected);
  }, [updateSelected, rawSelected]);

  const onDelete = useCallback((itemId) => {
    const newSelected = rawSelected.filter((item) => item.id !== itemId);
    updateSelected(newSelected);
  }, [updateSelected, rawSelected]);

  const onUpdate = useCallback((rowId, payload, fieldId) => {
    const updatedField = payload.customData?.[fieldId];
    if (!updatedField) return;

    const templateField = relevantBucketTemplate?.customFields?.fields
      ?.find((field) => field.id === fieldId);
    const fieldType = templateField?.type ?? updatedField.type;
    const response = getBucketCustomDataResponse(fieldType, updatedField);

    const newSelected = rawSelected.map((item) => {
      if (item.id !== rowId) return item;

      const existingFields = item.customData?.fields ?? [];
      const fieldIndex = existingFields.findIndex((field) => field.fieldId === fieldId);
      let fields;

      if (fieldIndex >= 0) {
        fields = existingFields.map((field, index) => (
          index === fieldIndex ? { ...field, response } : field
        ));
      } else {
        fields = [
          ...existingFields,
          {
            fieldId,
            type: fieldType,
            title: templateField?.title,
            response,
          },
        ];
      }

      return {
        ...item,
        customData: {
          ...(item.customData ?? {}),
          fields,
        },
      };
    });

    updateSelected(newSelected);
  }, [updateSelected, rawSelected, relevantBucketTemplate]);

  const tableColumns = useMemo(() => (
    getColumns({
      columns,
      isDisplay,
      requiredColumns,
      preventEdits,
      onDelete,
      onValueUpdated,
      relevantBucketTemplate,
      responding,
      onUpdate,
      relevantStatuses,
      columnPermissions,
      userPositionId,
      responses,
    })
  ), [
    columns,
    isDisplay,
    requiredColumns,
    preventEdits,
    onDelete,
    onValueUpdated,
    relevantBucketTemplate,
    responding,
    onUpdate,
    relevantStatuses,
    columnPermissions,
    userPositionId,
    responses,
  ]);

  return (
    <>
      <Row style={{ marginTop: showCondensedView ? 0 : 15 }}>
        <Row justify="space-between" style={{ width: '100%', marginBottom: 10 }}>
          <Col>
            {title}
          </Col>
          {!isDisplay && (
            <OnTraccrButton
              title={`Add ${relevantBucketTemplate?.name}`}
              onClick={toggleAddExistingBucketModal}
              icon={<PlusOutlined />}
              disabled={!shouldAddButtonBeEnabled}
            />
          )}
        </Row>
        { !showCondensedView || dataSource?.length ? (
          <Table
            className='form-table-preview'
            style={{ width: '100%', overflow: 'auto' }}
            columns={tableColumns}
            size="small"
            pagination={false}
            dataSource={enrichedDataSource}
            rowKey="id"
          />
        ) : (
          <DisplayText title={`No ${relevantBucketTemplate?.name} Selected`} style={{ marginBottom: 0 }} />
        )}
      </Row>
      {!isDisplay && (
        <AddExistingBucketModal
          title={`Add Existing ${relevantBucketTemplate?.name}`}
          visible={isAddExistingBucketModalVisible}
          onClose={toggleAddExistingBucketModal}
          bucketOptions={relevantBuckets}
          onSave={onAdd}
          submitValidation={submitValidation}
        />
      )}
    </>
  );
}

BucketTablePreview.propTypes = {
  title: PropTypes.node,
  buckets: PropTypes.array.isRequired,
  bucketTemplates: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  previewProps: PropTypes.object.isRequired,
  setPreviewProps: PropTypes.func.isRequired,
  isDisplay: PropTypes.bool.isRequired,
  id: PropTypes.string.isRequired,
  responses: PropTypes.object.isRequired,
  setResponses: PropTypes.func.isRequired,
  responding: PropTypes.bool.isRequired,
  showCondensedView: PropTypes.bool.isRequired,
  configProps: PropTypes.object.isRequired,
  statuses: PropTypes.object,
};

BucketTablePreview.defaultProps = {
  title: null,
  buckets: [],
  bucketTemplates: [],
  columns: [],
  previewProps: {},
  setPreviewProps: () => {},
  isDisplay: false,
  id: '',
  responses: {},
  setResponses: () => {},
  responding: false,
  showCondensedView: false,
  configProps: {},
  statuses: {},
};